import type {
  IContentEditor,
  ISitePage
} from './contracts';

/**
 * The invented content the four remaining mock screens are drawn over (§7.11).
 *
 * **Nothing here is real and nothing here is anybody's.** These screens have no endpoint, so
 * every row is made up — and the people are made up too, deliberately. Real names
 * appear in the requirement documents and in the agency's call notes, and seeding a fake bio,
 * a fake headshot or a fake authorship onto a real person makes a record that reads as genuine
 * and travels. The marketing team, the site's team page and the editor roster below are all
 * invented, and the screens say on themselves that they are (`shared.tsx`'s status strip).
 *
 * **The content is dated to the programme it belongs to.** The brand announcement is 15 October
 * 2026, content tooling 3 November, the full experience 14 December, and the first ranking
 * publishes in December — so the drafts in the queue are the ones that window would actually
 * hold. A fixture dated to a quiet week would make the queue screens look emptier than the thing
 * they are modelling, which is the point in the year with the most traffic through them.
 *
 * Dates are written as plain ISO strings rather than computed from today, so a screenshot taken
 * in December shows what a screenshot taken in October did and a reviewer comparing two runs is
 * comparing the layout rather than the clock.
 */

/** Who the four mock screens attribute a change to. `you` is the viewer. */
export const EDITORS: IContentEditor[] = [
  {
    accountId: 'you',
    name: 'Dana Whitfield',
    email: 'dana.whitfield@example.invalid',
    roles: ['Administrator']
  },
  {
    accountId: 'ed-2',
    name: 'Priya Raman',
    email: 'priya.raman@example.invalid',
    roles: ['Research staff']
  },
  {
    accountId: 'ed-3',
    name: 'Marcus Reyes',
    email: 'marcus.reyes@example.invalid',
    roles: ['Research staff']
  },
  {
    accountId: 'ed-4',
    name: 'Tomas Okafor',
    email: 'tomas.okafor@example.invalid',
    roles: ['Research staff']
  },
  {
    accountId: 'ed-5',
    name: 'Elena Costa',
    email: 'elena.costa@example.invalid',
    roles: ['Research staff']
  },
  {
    accountId: 'ed-6',
    name: 'Owen Baptiste',
    email: 'owen.baptiste@example.invalid',
    roles: ['Administrator']
  }
];

/** The viewer. One constant rather than a lookup, because every screen needs it. */
export const VIEWER_ID = 'you';

export const SITE_PAGES: ISitePage[] = [
  {
    id: 'pg-home',
    path: '/',
    name: 'Homepage',
    template: 'Home',
    inLaunchSet: true,
    blocks: [
      { id: 'b1', label: 'Hero statement', kind: 'text', value: 'Advisor excellence, defined and measured.' },
      {
        id: 'b2',
        label: 'Hero supporting line',
        kind: 'text',
        value: 'Independent research into the advisors institutions trust.'
      },
      {
        id: 'b3',
        label: 'Introduction',
        kind: 'rich',
        value:
          '<p>Anyone can list advisors. We define excellence, publish how we define it, and ' +
          'let the methodology be read in full.</p>'
      },
      { id: 'b4', label: 'Hero image', kind: 'image', value: 'The summit stage', mediaId: 'm-hero-summit' }
    ],
    seo: {
      title: 'Veritant — independent advisor research',
      description:
        'Independent research into the financial advisors institutions trust. Read the ' +
        'methodology and the independence charter in full.',
      socialImageId: 'm-social-default'
    },
    status: 'published',
    revision: 9,
    updatedAt: '2026-09-29T14:20:00Z',
    updatedBy: 'Dana Whitfield',
    heldBy: null,
    submittedAt: null,
    history: [
      { versionNo: 7, at: '2026-09-20T10:00:00Z', by: 'Dana Whitfield', action: 'saved' },
      { versionNo: 8, at: '2026-09-25T13:00:00Z', by: 'Elena Costa', action: 'saved' },
      { versionNo: 9, at: '2026-09-29T14:20:00Z', by: 'Dana Whitfield', action: 'published' }
    ]
  },
  {
    id: 'pg-charter',
    path: '/charter',
    name: 'Independence charter',
    template: 'Standard content',
    inLaunchSet: true,
    blocks: [
      { id: 'b1', label: 'Page title', kind: 'text', value: 'The independence charter' },
      {
        id: 'b2',
        label: 'Body',
        kind: 'rich',
        value:
          '<p>This charter states what we do not do.</p>' +
          '<h2>No payment for consideration</h2>' +
          '<p>No firm and no advisor can pay to be considered, ranked or placed.</p>' +
          '<h2>No advance sight</h2>' +
          '<p>No firm sees a ranking before it publishes.</p>'
      },
      { id: 'b3', label: 'Illustration', kind: 'image', value: 'Charter illustration', mediaId: 'm-charter-illus' }
    ],
    seo: {
      title: 'The independence charter | Veritant',
      description: 'What we do not do: no paid consideration, no advance sight, no commercial reach.',
      socialImageId: 'm-social-default'
    },
    status: 'published',
    revision: 5,
    updatedAt: '2026-09-28T11:00:00Z',
    updatedBy: 'Odile Renard',
    heldBy: null,
    submittedAt: null,
    history: [
      { versionNo: 4, at: '2026-09-22T09:00:00Z', by: 'Odile Renard', action: 'saved' },
      { versionNo: 5, at: '2026-09-28T11:00:00Z', by: 'Odile Renard', action: 'published' }
    ]
  },
  {
    id: 'pg-methodology',
    path: '/methodology',
    name: 'Methodology',
    template: 'Standard content',
    inLaunchSet: true,
    blocks: [
      { id: 'b1', label: 'Page title', kind: 'text', value: 'Methodology' },
      {
        id: 'b2',
        label: 'Body',
        kind: 'rich',
        value: '<p>How a nomination is assessed, and what each stage looks at.</p>'
      },
      { id: 'b3', label: 'Diagram', kind: 'image', value: 'Nomination to ranking', mediaId: 'm-method-diagram' }
    ],
    seo: {
      title: 'Methodology | Veritant',
      description: 'How a nomination is assessed, stage by stage.',
      socialImageId: 'm-social-default'
    },
    status: 'published',
    revision: 6,
    updatedAt: '2026-09-27T15:30:00Z',
    updatedBy: 'Tomas Okafor',
    heldBy: null,
    submittedAt: null,
    history: [
      { versionNo: 5, at: '2026-09-24T10:00:00Z', by: 'Tomas Okafor', action: 'saved' },
      { versionNo: 6, at: '2026-09-27T15:30:00Z', by: 'Tomas Okafor', action: 'published' }
    ]
  },
  {
    id: 'pg-team',
    path: '/team',
    name: 'Team',
    template: 'People',
    inLaunchSet: true,
    blocks: [
      { id: 'b1', label: 'Page title', kind: 'text', value: 'The research team' },
      {
        id: 'b2',
        label: 'Introduction',
        kind: 'rich',
        value: '<p>The people who produce the research.</p>'
      }
    ],
    seo: {
      title: 'The research team | Veritant',
      description: 'The people who produce the research.',
      socialImageId: 'm-social-default'
    },
    status: 'published',
    revision: 3,
    updatedAt: '2026-09-10T11:05:00Z',
    updatedBy: 'Dana Whitfield',
    heldBy: null,
    submittedAt: null,
    history: [
      { versionNo: 2, at: '2026-09-08T09:20:00Z', by: 'Priya Raman', action: 'saved' },
      { versionNo: 3, at: '2026-09-10T11:05:00Z', by: 'Dana Whitfield', action: 'published' }
    ]
  },
  {
    id: 'pg-news',
    path: '/news',
    name: 'News and insights',
    template: 'Index',
    inLaunchSet: true,
    blocks: [
      { id: 'b1', label: 'Page title', kind: 'text', value: 'News and insights' },
      {
        id: 'b2',
        label: 'Introduction',
        kind: 'rich',
        value: '<p>Announcements, press and writing from the research team.</p>'
      }
    ],
    seo: {
      title: 'News and insights | Veritant',
      description: 'Announcements, press and writing from the research team.',
      socialImageId: 'm-social-default'
    },
    status: 'published',
    revision: 2,
    updatedAt: '2026-09-12T10:00:00Z',
    updatedBy: 'Priya Raman',
    heldBy: null,
    submittedAt: null,
    history: [
      { versionNo: 1, at: '2026-09-05T10:00:00Z', by: 'Priya Raman', action: 'created' },
      { versionNo: 2, at: '2026-09-12T10:00:00Z', by: 'Priya Raman', action: 'published' }
    ]
  },
  {
    id: 'pg-events',
    path: '/events',
    name: 'Events',
    template: 'Index',
    inLaunchSet: false,
    blocks: [
      { id: 'b1', label: 'Page title', kind: 'text', value: 'Events' },
      {
        id: 'b2',
        label: 'Introduction',
        kind: 'rich',
        value: '<p>Summits, roundtables and briefings.</p>'
      }
    ],
    seo: { title: '', description: '', socialImageId: null },
    status: 'draft',
    revision: 1,
    updatedAt: '2026-09-30T08:00:00Z',
    updatedBy: 'Marcus Reyes',
    heldBy: null,
    submittedAt: null,
    history: [{ versionNo: 1, at: '2026-09-30T08:00:00Z', by: 'Marcus Reyes', action: 'created' }]
  },
  {
    id: 'pg-careers',
    path: '/careers',
    name: 'Careers',
    template: 'Standard content',
    inLaunchSet: false,
    blocks: [
      { id: 'b1', label: 'Page title', kind: 'text', value: 'Careers' },
      { id: 'b2', label: 'Body', kind: 'rich', value: '<p>Open roles at the research practice.</p>' }
    ],
    seo: { title: '', description: '', socialImageId: null },
    status: 'draft',
    revision: 1,
    updatedAt: '2026-09-26T13:00:00Z',
    updatedBy: 'Dana Whitfield',
    heldBy: null,
    submittedAt: null,
    history: [{ versionNo: 1, at: '2026-09-26T13:00:00Z', by: 'Dana Whitfield', action: 'created' }]
  }
];

